import { PriceRule, FrequencyInterval } from 'skedyul-db/enums'
import dayjs from 'dayjs'

export const parsePriceRule = (priceRule: PriceRule) => {
  let label = 'per hour',
    divisor = 1,
    shortLabel = '/ hr'

  switch (priceRule) {
    case PriceRule.PER_HOUR:
      label = 'per hour'
      shortLabel = '/ hr'
      divisor = 60
      break
    case PriceRule.PER_30_MINUTES:
      label = 'per 30 minutes'
      shortLabel = '/ 30m'
      divisor = 30
      break
    case PriceRule.PER_10_MINUTES:
      label = 'per 10 minutes'
      shortLabel = '/ 10m'
      divisor = 10
      break
    case PriceRule.PER_15_MINUTES:
      label = 'per 15 minutes'
      shortLabel = '/ 15m'
      divisor = 15
      break
    case PriceRule.PER_5_MINUTES:
      label = 'per 5 minutes'
      shortLabel = '/ 5m'
      divisor = 5
      break
    case PriceRule.PER_MINUTE:
      label = 'per minute'
      shortLabel = '/ m'
      divisor = 1
      break
    case PriceRule.PER_SESSION:
      label = 'per session'
      shortLabel = '/ session'
      divisor = 1
      break
  }

  return { label, divisor, shortLabel }
}

export const parseFrequencyInterval = (
  frequencyInterval: FrequencyInterval,
) => {
  let label = 'once off'

  switch (frequencyInterval) {
    case FrequencyInterval.DAILY:
      label = 'daily'
      break
    case FrequencyInterval.WEEKLY:
      label = 'weekly'
      break
    case FrequencyInterval.FORTNIGHTLY:
      label = 'every 2 weeks'
      break
    case FrequencyInterval.MONTHLY:
      label = 'monthly'
      break
    case FrequencyInterval.YEARLY:
      label = 'yearly'
      break
  }

  return { label }
}

export const calculatePayableAmount = (
  service: {
    priceRule: PriceRule
    price: { amount: number }
    minSessionLengthInMinutes: number
  },
  startAt: Date,
  endAt: Date,
) => {
  if (service.priceRule == PriceRule.PER_SESSION) {
    return service.price.amount
  }

  let duration = dayjs(endAt).diff(startAt, 'minutes')

  if (duration < service.minSessionLengthInMinutes) {
    duration = service.minSessionLengthInMinutes
  }

  switch (service.priceRule) {
    case PriceRule.PER_MINUTE:
      // using day js to calculate the difference in minutes
      return duration * service.price.amount
    case PriceRule.PER_HOUR:
      return Math.ceil(duration / 60) * service.price.amount
    case PriceRule.PER_5_MINUTES:
      return Math.ceil(duration / 5) * service.price.amount
    case PriceRule.PER_10_MINUTES:
      return Math.ceil(duration / 10) * service.price.amount
    case PriceRule.PER_15_MINUTES:
      return Math.ceil(duration / 15) * service.price.amount
    case PriceRule.PER_30_MINUTES:
      return Math.ceil(duration / 30) * service.price.amount
    default:
      return null
  }
}

export const getBillableTime = (
  service: { minSessionLengthInMinutes: number },
  startAt: Date,
  endAt: Date,
) => {
  const duration = dayjs(endAt).diff(startAt, 'minutes')

  if (duration < service.minSessionLengthInMinutes) {
    return {
      value: duration,
      label: `${service.minSessionLengthInMinutes} minutes (min. session)`,
    }
  }

  return { value: duration, label: `${duration} minutes` }
}

export const calculateTotal = (
  service: {
    priceRule: PriceRule
    price: { amount: number }
    minSessionLengthInMinutes: number
  },
  depositFragment: number,
  startAt: Date,
  endAt: Date,
  items: { unitPrice: { amount: number }; quantity: number }[],
) => {
  const payable = calculatePayableAmount(service, startAt, endAt)

  // sum all the items
  const itemsTotal = items.reduce((acc, item) => {
    return acc + item.unitPrice.amount * item.quantity
  }, 0)

  if (payable == null || depositFragment == null) {
    return null
  }

  return payable + depositFragment + itemsTotal
}
